import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AccountsService } from '@services/accounts.service';
import { AuthenticationService } from '@services/authentication.service';
import { RulesDataService } from '@services/rules-data.service';
import { combineLatest, concat, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { VirtualSensor } from '@services/virtual-sensors.service';
import { DataPoint, DatapointsDataService } from '@services/datapoints-data.service';
import { Data } from '@angular/router';

export class editVirtualSensorDialogData {
	virtualSensor?: VirtualSensor
}

@Component({
	selector: 'app-edit-virtual-sensor',
	templateUrl: './edit-virtual-sensor.component.html',
	styleUrl: './edit-virtual-sensor.component.scss'
})
export class EditVirtualSensorComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** Form Group */
	dialogForm: FormGroup;

	/** virtualSensor */
	public virtualSensor?: VirtualSensor;

	/** dpFields */
	public dpFields: Object = { text: "DPTitle", value: "DataPointID" };

	/** datapoints */
	public datapoints: DataPoint[] = [];

	/** selectedDatapoints */
	public selectedDatapoints: DataPoint[] = [];

	/** selectedDpIDs */
	public selectedDpIDs: number[] = [];

	/** datapointNumberOptions */
	public datapointNumberOptions = [
		{ text: "[1]", value: 1 },
		{ text: "[2]", value: 2 },
		{ text: "[3]", value: 3 },
		{ text: "[4]", value: 4 },
		{ text: "[5]", value: 5 },
	];

	/** datapointNumberFields */
	public datapointNumberFields: Object = { text: "text", value: "value" };

	/** parsedInputDataPoints */
	public parsedInputDataPoints: any;

	/** inputDataPoints */
	public inputDataPoints: any;

	/** conversionOptions */
	public conversionOptions: {label: string, value: string}[] = [
		{label: "+", value: "+"},
		{label: "-", value: "-"},
		{label: "*", value: "*"},
		{label: "/", value: "/"},
		{label: "%", value: "%"},
		{label: "<", value: "<"},
		{label: ">", value: ">"},
		{label: ">=", value: ">="},
		{label: "<=", value: "<="},
		{label: "==", value: "=="},
		{label: "(", value: "("},
		{label: ")", value: ")"},
		{label: "?", value: "?"},
		{label: ":", value: ":"},
	];

	/** conversionText */
	public conversionText: string = "";

	/** customInputValue */
	public customInputValue: number = 0;

	/**
	 * constructor
	 * @param fb fb 
	 * @param dialogRef dialogRef 
	 * @param globalFunctions globalFunctions 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param rulesDataService rulesDataService 
	 * @param authenticationService authenticationService 
	 * @param accountsService accountsService 
	 * @param dialogData dialogData 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditVirtualSensorComponent>,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		private rulesDataService: RulesDataService,
		private authenticationService: AuthenticationService,
		private accountsService: AccountsService,
		private datapointsDataService: DatapointsDataService,
		@Inject(MAT_DIALOG_DATA) public dialogData: editVirtualSensorDialogData,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			filter(([selectedAccountGUID, selectedOrganisationUID, currentUser]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID && !!currentUser
			)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, currentUser]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;

				this.virtualSensor = dialogData.virtualSensor;

				this.GetDataPoints(this.selectedAccountUID, this.selectedOrganisationUID);

				if (this.virtualSensor) {
					const inputDPs = JSON.parse(this.virtualSensor.InputDataPoints);
					this.parsedInputDataPoints = inputDPs;
					this.inputDataPoints = this.parsedInputDataPoints;
					let idString: string = "";
					Object.entries(inputDPs).forEach((item) => idString += `${item[1]},`);
					idString = idString.slice(0, idString.length - 1);
					this.GetDataPointsFromID(this.selectedAccountUID, this.selectedOrganisationUID, idString);
					this.conversionText = this.virtualSensor.Conversion;
				} else {
					this.inputDataPoints = {};
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		if (this.virtualSensor) {
			this.dialogForm = this.fb.group({
				virtualSensorName: [this.virtualSensor.VirtualSensorName, Validators.required],
				inputDatapoints: [this.virtualSensor.InputDataPoints, Validators.required],
				conversion: [this.virtualSensor.Conversion],
				SI: [this.virtualSensor.SI]
			});
		} else {
			this.dialogForm = this.fb.group({
				virtualSensorName: ['', Validators.required],
				inputDatapoints: [[], Validators.required],
				conversion: [''],
				SI: [''],
			});
		}
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * GetDataPoints
	 * @param accountID accountID 
	 * @param orgUID orgUID 
	 * @returns 
	 */
	async GetDataPoints(accountID: string, orgUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.datapointsDataService.getDataPoints$(accountID, orgUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				this.datapoints = dataPoints;
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * GetDataPointsFromID
	 * @param accountID accountID 
	 * @param orgUID orgUID 
	 */
	async GetDataPointsFromID(accountID: string, orgUID: string, ids: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.datapointsDataService.getDataPoints$(accountID, orgUID, ids).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				this.selectedDatapoints = dataPoints;
				this.selectedDpIDs = this.selectedDatapoints.map((x: DataPoint) => x.DataPointID);
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * getValueForDP
	 * @param datapoint datapoint 
	 */
	getValueForDP(datapoint: DataPoint): number {
		if (this.virtualSensor) {
			return parseInt(Object.entries(this.inputDataPoints).find((item) => item[1] == `${datapoint.DataPointID}`)?.[0] ?? "0");
		} else {
			if(this.inputDataPoints) {
				return parseInt(Object.entries(this.inputDataPoints).find((item) => item[1] == `${datapoint.DataPointID}`)?.[0] ?? "0");
			} else {
				return 0;
			}
		}
	}

	/**
	 * datapointChoiceChange
	 * @param event event 
	 */
	datapointChoiceChange(event: any) {
		if (event.isInteracted) {
			this.selectedDpIDs = event.value;
			this.selectedDatapoints = this.datapoints.filter((x: DataPoint) => this.selectedDpIDs.includes(x.DataPointID));
		}
	}

	/**
	 * dataPointNumberChange
	 * @param event event 
	 */
	dataPointNumberChange(event: any, datapointID: any) {
		if (event.previousItemData) delete this.inputDataPoints[event.previousItemData.value];
		this.inputDataPoints[event.itemData.value] = datapointID;
		this.dialogForm.get("inputDatapoints")?.setValue(JSON.stringify(this.inputDataPoints));
	}

	/**
	 * writeToConversion
	 * @param value value 
	 */
	writeToConversion(value: string) {
		this.conversionText = this.conversionText.concat(` ${value}`).trim();
		this.updateConversionData();
	}

	/**
	 * writeLabelToConversion
	 * @param value value 
	 */
	writeLabelToConversion(value: number) {
		this.conversionText = this.conversionText.concat(` [${value}]`).trim();
		this.updateConversionData();
	}

	/**
	 * updateConversionData
	 */
	updateConversionData() {
		this.dialogForm.get("conversion")?.setValue(this.conversionText);
	}

	/**
	 * backspaceConversion
	 */
	backspaceConversion() {
		this.conversionText = this.conversionText.slice(0, this.conversionText.length - 1).trim();
	}
}
